import { memo, useMemo } from "react";
import Slider from "react-slick";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { POPULAR_CATEGORIES_SETTINGS } from "../../constants/sliderSettings";
import type { Category, CoursesByCategory } from "../../types/home";

import slider_5 from "../../Assets/Images/slider_5.webp";
import slider_11 from "../../Assets/Images/slider_11.webp";
import popular_cat_img from "../../Assets/Images/languagelearning.webp";
import floatingImg1 from "../../Assets/Images/Group 18.png";
import floatingImg2 from "../../Assets/Images/Elements.png";

interface CategoryCardConfig {
  image: string;
  label: string;
  count: string;
  alt: string;
  /** Bootstrap column classes used in the desktop grid (preserved verbatim). */
  desktopColClassName: string;
}

/** Typed identity helper: validates each value while preserving literal keys. */
const asCategoryCards = <T extends Record<string, CategoryCardConfig>>(
  config: T
): T => config;

/** Curated cards keyed by the (lower-cased) category title returned by the API. */
const CATEGORY_CARDS = asCategoryCards({
  executive: {
    image: slider_11,
    label: "Executive Courses",
    count: "30+ Courses",
    alt: "Executive",
    desktopColClassName:
      "col-12 col-sm-5 col-md-3 my-2 text-start text-md-center mx-auto",
  },
  vocational: {
    image: slider_5,
    label: "Vocational Courses",
    count: "45+ Courses",
    alt: "Vocational",
    desktopColClassName: "col-12 col-sm-5 col-md-3 my-2 text-center mx-auto",
  },
  "language learning": {
    image: popular_cat_img,
    label: "Language Learning",
    count: "12+ Courses",
    alt: "Language Learning",
    desktopColClassName: "col-12 col-sm-5 col-md-3 my-2 text-center mx-auto",
  },
});

type CategoryCardKey = keyof typeof CATEGORY_CARDS;

interface VisibleCard {
  id: number;
  href: string;
  config: CategoryCardConfig;
}

interface PopularCategoriesProps {
  categories: Category[];
  courses: CoursesByCategory;
}

const CategoryCard = ({
  card,
  colClassName,
}: {
  card: VisibleCard;
  colClassName: string;
}) => (
  <div className={colClassName}>
    <a href={card.href}>
      <div className="w-100 border popular_cat">
        <img src={card.config.image} className="w-100" alt={card.config.alt} />
      </div>
    </a>
    <a href={card.href} className="link-dark">
      <h6 className="fw-bold mt-4">{card.config.label}</h6>
    </a>
    <p className="fw-normal text-red">{card.config.count}</p>
  </div>
);

const BrowseAllButton = ({ href }: { href: string }) => (
  <a
    href={href}
    className="btn btn-lg btn-br mt-3 text-center _hover  text-white"
    style={{ backgroundColor: "#5DC28A" }}
  >
    <div className="d-flex justify-content-center align-items-center">
      <FontAwesomeIcon icon={solid("circle-plus")} />
      <small className="ms-2"> BROWSE ALL</small>
    </div>
  </a>
);

const PopularCategories = ({ categories, courses }: PopularCategoriesProps) => {
  const isReady =
    categories.length > 0 && Object.keys(courses).length > 2;

  const visibleCards = useMemo<VisibleCard[]>(() => {
    if (!isReady) {
      return [];
    }
    return categories.reduce<VisibleCard[]>((acc, category) => {
      const config = CATEGORY_CARDS[category.title.toLowerCase() as CategoryCardKey];
      if (config) {
        acc.push({
          id: category.id,
          href: `/courses?id=${courses[category.id].id}&category=${category.id}`,
          config,
        });
      }
      return acc;
    }, []);
  }, [isReady, categories, courses]);

  const browseAllHref = isReady
    ? `/courses?id=${courses[categories[0].id].id}&category=${categories[0].id} `
    : "";

  return (
    <div className="w-100 my-5">
      <div className="container-xxl p-0 position-relative">
        <img
          src={floatingImg1}
          className="popular_cat_f_img1"
          alt="floating_img1"
        />
        <img
          src={floatingImg2}
          className="popular_cat_f_img2"
          alt="floating_img2"
        />

        <div className="row mx-0">
          <div className="col-12 col-md-8 mb-5 mx-md-auto m-0 text-start text-md-center">
            <h5 className="fw-bold">Popular Categories</h5>
            <p className="fw-light text-break">
              Sekho’s most popular categories, Let get started try watching some
              to learn executive skills, vocational skills or language learning.
            </p>
          </div>
        </div>

        {/* Desktop grid */}
        <div className="row mx-0 d-none d-lg-flex">
          {visibleCards.map((card) => (
            <CategoryCard
              key={card.id}
              card={card}
              colClassName={card.config.desktopColClassName}
            />
          ))}
          <div className="col-12 text-center">
            <BrowseAllButton href={browseAllHref} />
          </div>
        </div>

        {/* Mobile carousel */}
        <div className="w-100 d-block d-lg-none overflow-hidden">
          <Slider {...POPULAR_CATEGORIES_SETTINGS}>
            {visibleCards.map((card) => (
              <CategoryCard key={card.id} card={card} colClassName="my-2" />
            ))}
          </Slider>
          <div className="w-100 text-center">
            <BrowseAllButton href={browseAllHref} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default memo(PopularCategories);
